export const JOINT_TYPE_OPTIONS = [
  { kind: "hinge", label: "Hinge" },
  { kind: "slide", label: "Slide" },
  { kind: "ball", label: "Ball" },
  { kind: "free", label: "Free" },
];

export const NON_FREE_JOINT_TYPE_OPTIONS = JOINT_TYPE_OPTIONS.filter(
  (option) => option.kind !== "free"
);

export const getJointRefUuid = (entry) => (typeof entry === "string" ? entry : entry?.uuid);

export function bodyHasParent(bodies, bodyUuid) {
  if (!bodyUuid) return false;
  return Object.values(bodies || {}).some(
    (body) =>
      Array.isArray(body?.children) &&
      body.children.some((childUuid) => typeof childUuid === "string" && childUuid === bodyUuid)
  );
}

export function getSiblingJointKindsForBody({ bodies, joints, bodyUuid, excludeJointUuid = null }) {
  const refs = Array.isArray(bodies?.[bodyUuid]?.joints) ? bodies[bodyUuid].joints : [];
  return refs
    .map(getJointRefUuid)
    .filter((uuid) => uuid && uuid !== excludeJointUuid)
    .map((uuid) => joints?.[uuid]?.kind)
    .filter(Boolean);
}

export function getJointTypeDisabledReason({
  kind,
  bodies,
  joints,
  bodyUuid,
  excludeJointUuid = null,
  currentKind = null,
}) {
  if (!kind || !bodyUuid) return "Choose a body before adding a joint.";
  if (kind === currentKind) return "";

  const siblingKinds = getSiblingJointKindsForBody({
    bodies,
    joints,
    bodyUuid,
    excludeJointUuid,
  });
  const hasParent = bodyHasParent(bodies, bodyUuid);
  const hasSiblingJoints = siblingKinds.length > 0;
  const siblingHasFree = siblingKinds.includes("free");
  const siblingHasBall = siblingKinds.includes("ball");
  const siblingHasRotationalJoint = siblingKinds.includes("ball") || siblingKinds.includes("hinge");

  if (kind === "free" && (hasParent || hasSiblingJoints)) {
    return "Free joints require a root body with no existing joints.";
  }
  if (kind !== "free" && siblingHasFree) {
    return "This body already moves freely in physics. Create a child body to add hinges, sliders, or other joints.";
  }
  if (kind === "ball" && siblingHasRotationalJoint) {
    return "Ball joints cannot share a body with hinge or ball joints.";
  }
  if (kind === "hinge" && siblingHasBall) {
    return "Hinge joints cannot share a body with a ball joint.";
  }
  return "";
}

export function getJointTypeMenuOptions({ includeFree = true, ...params }) {
  const options = includeFree ? JOINT_TYPE_OPTIONS : NON_FREE_JOINT_TYPE_OPTIONS;
  return options.map((option) => {
    const reason = getJointTypeDisabledReason({ ...params, kind: option.kind });
    return reason ? { ...option, disabled: true, description: reason } : option;
  });
}

export function getJointMoveDisabledReason({
  joint,
  jointUuid = joint?.uuid ?? null,
  bodies,
  joints,
  sourceBodyUuid = null,
  targetBodyUuid,
}) {
  if (!joint?.kind || !targetBodyUuid) return "Choose a target body.";
  if (sourceBodyUuid && sourceBodyUuid === targetBodyUuid) return "";
  return getJointTypeDisabledReason({
    kind: joint.kind,
    bodies,
    joints,
    bodyUuid: targetBodyUuid,
    excludeJointUuid: jointUuid,
  });
}
